import { WebStorageStateStore } from 'oidc-client-ts'
import type { AuthProviderProps } from 'react-oidc-context'
import { env } from '@/env'

/**
 * OIDC config for the Spaceti Passport IdP.
 *
 * Refresh-token rotation is a Passport-side concern — the Lobby only needs
 * `offline_access` in its scope set (sourced from env) and
 * `automaticSilentRenew: true` to kick the built-in silent-renew timer.
 *
 * `extraQueryParams.passport_entry = 'email'` is a Passport-specific hint
 * that pre-selects the e-mail entry path on the sign-in screen so users
 * land on the right method without an extra click.
 */
export const oidcConfig: AuthProviderProps = {
  authority: env.VITE_PASSPORT_ISSUER,
  client_id: env.VITE_OIDC_CLIENT_ID,
  redirect_uri: `${env.VITE_LOBBY_ORIGIN}/callback`,
  post_logout_redirect_uri: `${env.VITE_LOBBY_ORIGIN}/signed-out`,
  response_type: 'code',
  scope: env.VITE_OIDC_SCOPE,
  automaticSilentRenew: true,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  extraQueryParams: { passport_entry: 'email' },
  /**
   * After the AuthProvider exchanges `?code=…&state=…` for tokens, strip
   * those params from the URL. Without this, React 18 StrictMode's double
   * effect invocation (and any re-render hitting AuthProvider's callback
   * detection) sees the same `code` in the URL and retries the exchange —
   * auth codes are single-use, so the retry fails and the session never
   * flips to authenticated, leaving /callback stuck on the spinner.
   *
   * `replaceState` (not `pushState`) keeps the callback out of history.
   * We only rewrite the path + query; `CallbackPage`'s effect then runs
   * `navigate('/', { replace: true })` to route into the Lobby.
   */
  onSigninCallback: () => {
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname,
    )
  },
}
